import './style.scss';

import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { classNames } from 'primereact/utils';
import ReactRouterPrompt from 'react-router-prompt';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  createProjectDeadlineExtensions as create,
  updateProjectDeadlineExtensions as update,
  uploadExtendFile,
  deleteExtendFile,
  submitProjectDeadlineExtensions,
  getById,
  downloadProjectDeadlineFile,
} from 'apis/projectDeadlineExtensions.api';
import { toast } from 'layout';
import { TOAST, PROJECT_ROLES, ADJUSTMENT_COMMENTS } from 'constant';
import { DatePicker, TextareaInput } from 'components/FormControl';
import CustomFileInput from 'components/FormControl/CustomFileInput';
import { formatFileName, convertToPascalCase, mapError } from 'utils/func';
import { getProjectById } from 'apis/project.api';
import Loading from 'components/Loading';
import UploadAppendixFiles from 'components/UploadAppendixFiles';
import ReplyAdjustmentCommentModal from 'features/Project/components/ReplyAdjustmentCommentModal';
import { useGetLastAdjustmentComments } from 'utils/hooks/adjustmentComments';
import { HeaderInfo, GeneralInfo, DownloadFile } from '../Shared';
import { projectDeadlineExtensionsUpdateSchema } from '../../validation';

export default function ProjectDeadlineExtensionsForm() {
  const { t } = useTranslation();
  const location = useLocation();
  const { isRequestedEdit } = location.state || {};
  const { projectId, projectDeadlineExtensionId } = useParams();
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({ mode: 'onChange', resolver: yupResolver(projectDeadlineExtensionsUpdateSchema) });
  const { isDirty, dirtyFields } = useFormState({ control });

  const [isEdit, setIsEdit] = useState(!!projectDeadlineExtensionId);
  const [isRequestedEditUsed, setIsRequestedEditUsed] = useState(isRequestedEdit);
  const [appendixeArray, setAppendixeArray] = useState([]);
  const [appendixFilesDelete, setAppendixFilesDelete] = useState([]);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmitReviseModalOpen, setIsSubmitReviseModalOpen] = useState(false);
  const [id, setId] = useState(projectDeadlineExtensionId ?? null);

  const fileUrl = watch('fileUrl');
  const { data: project, isLoading: isProjectLoading } = useQuery(
    ['getProjectById', projectId],
    () => getProjectById(projectId)
  );
  const owner = useMemo(
    () =>
      project?.data?.projectProposal?.projectProposalContacts?.find(
        (pp) => pp.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? null,
    [project]
  );

  const { data: _data, refetch } = useQuery(
    ['getProjectDeadlineExtensionsById', id],
    () => getById(id),
    { enabled: isEdit && !!id }
  );
  const projectDeadlineExtensions = useMemo(() => _data?.data ?? [], [_data]);

  const { data: lastComment } = useGetLastAdjustmentComments(
    {
      adjustmentName: ADJUSTMENT_COMMENTS.PROJECT_DEADLINE_EXTENSION,
      adjustmentId: projectDeadlineExtensionId,
    },
    {
      enabled: !!isRequestedEdit,
    }
  );

  const { mutateAsync: mutateAsyncCreate } = useMutation(create);
  const { mutateAsync: mutateAsyncUpdate } = useMutation(update);
  const { mutateAsync: mutateAsyncUploadFile, isLoading: isUploadFileLoading } =
    useMutation(uploadExtendFile);
  const { mutateAsync: mutateAsyncDeleteFile } = useMutation(deleteExtendFile);
  const { mutate: mutateSubmitExtend, isLoading: isSubmitLoading } = useMutation(
    submitProjectDeadlineExtensions
  );
  const { mutate: mutateDownloadFile } = useMutation(downloadProjectDeadlineFile);

  const onSubmit = async (data) => {
    const dataSender = {
      reason: data?.reason,
      extendDate: data?.extendDate,
      projectId,
    };

    let res;
    let idUsed;
    let toastMessage = '';

    if (isEdit) {
      res = await mutateAsyncUpdate({ id, body: dataSender });
      toastMessage = t('extendImplementationTime.R07.updateSuccess');
      idUsed = id;
    } else {
      res = await mutateAsyncCreate(dataSender);
      toastMessage = t('extendImplementationTime.R07.createSuccess');
      idUsed = res?.data?.id;
    }
    const dataUpload = new FormData();

    dataUpload.append('id', idUsed);
    dataUpload.append('projectId', projectId);
    if (data?.decisionFile) {
      dataUpload.append('decisionFile', data?.decisionFile);
    }
    data?.appendixFiles?.forEach((file) => {
      dataUpload.append('appendixFiles', file);
    });
    await mutateAsyncUploadFile({ id: idUsed, body: dataUpload });
    if (appendixFilesDelete?.length > 0) {
      const formDeleteContractFileData = {};
      formDeleteContractFileData.appendixIds = appendixFilesDelete;
      await mutateAsyncDeleteFile({ id: idUsed, body: formDeleteContractFileData });
      setAppendixFilesDelete([]);
    }
    toast(TOAST.SUCCESS, toastMessage);

    reset();
    refetch();
    if (!isEdit) {
      setIsEdit(true);
      setId(res?.data?.id);
    }
    if (data?.appendixFiles?.length > 0) {
      reset({ appendixFiles: [] });
    }
  };

  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      { path, id },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  useEffect(() => {
    if (projectDeadlineExtensions) {
      if (projectDeadlineExtensions?.extendDate) {
        setValue('extendDate', new Date(projectDeadlineExtensions?.extendDate));
      }
      setValue('reason', projectDeadlineExtensions?.reason);
      setValue('fileUrl', projectDeadlineExtensions?.fileUrl);
      if (projectDeadlineExtensions?.extendImplementationTimeAppendixes?.length > 0) {
        setAppendixeArray(projectDeadlineExtensions?.extendImplementationTimeAppendixes);
      }
    }
  }, [projectDeadlineExtensions]);

  const handleSubmitProjectDeadlineExtension = () => {
    mutateSubmitExtend(id, {
      onSuccess: () => {
        if (isRequestedEdit) {
          toast(TOAST.SUCCESS, t('extendImplementationTime.R07.submitReviseSuccess'));
          setIsRequestedEditUsed(false);
          setIsSubmitReviseModalOpen(false);
        } else {
          toast(TOAST.SUCCESS, t('extendImplementationTime.R07.submitSuccess'));
          setIsSubmitModalOpen(false);
        }
        refetch();
      },
      onError: (error) => {
        toast(TOAST.ERROR, mapError(t, error));
      },
    });
  };

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsSubmitModalOpen(false)}
        autoFocus
      />
      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          handleSubmitProjectDeadlineExtension();
        }}
      />
    </div>
  );

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />

      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const formatLastComment = () => (
    <div>
      {t('projectAdjustment.commentFromSpecialist')}
      {': '}
      {lastComment?.comment}
    </div>
  );

  useEffect(() => {
    if (project?.data?.endDate) {
      setValue('endDate', project?.data?.endDate);
    }
  }, [project]);
  return (
    <>
      {(isProjectLoading || isUploadFileLoading) && <Loading />}
      <div className="p-6 detail-extend-implementation-time-form">
        <Dialog
          header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
          visible={isSubmitModalOpen}
          position="center"
          style={{ width: '400px' }}
          onHide={() => setIsSubmitModalOpen(false)}
          draggable={false}
          footer={footerConfirmationSubmit}
        >
          <Message
            className="mt-4 w-full"
            severity="warn"
            text={t('extendImplementationTime.message')}
          />
        </Dialog>
        <DownloadFile id={id} projectId={projectId} />
        <div className="p-5">
          <HeaderInfo />
          {lastComment && <Message severity="info" text={formatLastComment} />}
          <form autoComplete="false" onSubmit={handleSubmit(onSubmit)}>
            {/* info project */}
            <div className="mt-4">
              <GeneralInfo />

              <div className="p-fluid mt-4 text-lg">
                <div className="xl:w-3 lg:w-4 md:w-5 sm:w-6">
                  <DatePicker
                    name="extendDate"
                    label={t('extendImplementationTime.R07.implementationTimeSuggestExtend.title')}
                    control={control}
                    errors={errors}
                    isRequired
                  />
                </div>

                <TextareaInput
                  name="reason"
                  label={t('extendImplementationTime.R07.reason')}
                  control={control}
                  errors={errors}
                  row={5}
                  isRequired
                />
              </div>
            </div>

            <div className="col-12 text-lg">
              <CustomFileInput
                name="decisionFile"
                label={t('extendImplementationTime.file')}
                accept=".pdf"
                acceptOnly="pdfOnly"
                acceptFileMessage="Chỉ chấp nhận file pdf"
                defaultNameFile={`${convertToPascalCase(owner?.fullname ?? '')}-Giahan.pdf`}
                control={control}
                errors={errors}
                isRequired
              />
              {fileUrl && (
                <span
                  className="underline"
                  style={{ color: 'blue', cursor: 'pointer' }}
                  onClick={() => handleDownloadFile(fileUrl)}
                >
                  {formatFileName(typeof fileUrl === 'string' ? fileUrl : fileUrl?.name)}
                </span>
              )}
            </div>

            <div className="col-12">
              <UploadAppendixFiles
                control={control}
                errors={errors}
                data={{ id: project?.data?.topicCode, name: 'PhuLuc' }}
                appendixeArray={appendixeArray}
                handleDownloadFile={handleDownloadFile}
                setAppendixFilesDelete={setAppendixFilesDelete}
                setAppendixeArray={setAppendixeArray}
                disabled={!!projectDeadlineExtensions?.isSubmit}
              />
            </div>

            <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
              {({ isActive, onConfirm, onCancel }) => (
                <Dialog
                  visible={isActive}
                  header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
                  position="center"
                  style={{ width: '300px' }}
                  onHide={onCancel}
                  draggable={false}
                  footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
                >
                  <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
                </Dialog>
              )}
            </ReactRouterPrompt>

            <div className="flex justify-content-end mt-6 gap-4">
              <Button
                className="w-10rem"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                label={t('extendImplementationTime.comeBack')}
                comeBack
                severity="danger"
                type="button"
                onClick={() => {
                  navigate(-1);
                }}
              />
              <Button
                type="submit"
                disabled={
                  (!(isDirty && Object.keys(dirtyFields).length) &&
                    appendixFilesDelete?.length === 0) ||
                  (projectDeadlineExtensions?.requestedAt && !isRequestedEditUsed)
                }
                className=" w-10rem "
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                {t('extendImplementationTime.save')}
              </Button>

              {!isRequestedEdit ? (
                <Button
                  type="button"
                  className="btn-control w-16rem"
                  icon={classNames(
                    'pi mr-1 ',
                    { 'pi-spin pi-spinner': isSubmitLoading },
                    { 'pi pi-check': !isSubmitLoading }
                  )}
                  disabled={
                    !projectId ||
                    !id ||
                    (isDirty && Object.keys(dirtyFields).length) ||
                    (projectDeadlineExtensions?.requestedAt && !isRequestedEditUsed)
                  }
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  severity="success"
                  onClick={() => {
                    setIsSubmitModalOpen(true);
                  }}
                >
                  {t('extendImplementationTime.create')}
                </Button>
              ) : (
                <Button
                  type="button"
                  className="btn-control w-14rem"
                  icon={classNames(
                    'pi mr-1 ',
                    { 'pi-spin pi-spinner': isSubmitLoading },
                    { 'pi pi-check': !isSubmitLoading }
                  )}
                  disabled={
                    !projectId ||
                    !id ||
                    (isDirty && Object.keys(dirtyFields).length) ||
                    (projectDeadlineExtensions?.requestedAt && !isRequestedEditUsed)
                  }
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  severity="warning"
                  onClick={() => {
                    setIsSubmitReviseModalOpen(true);
                  }}
                >
                  {t('extendImplementationTime.revise')}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>

      <ReplyAdjustmentCommentModal
        visible={isSubmitReviseModalOpen}
        onClose={() => setIsSubmitReviseModalOpen(false)}
        adjustmentCommentId={lastComment?.id}
        onSubmit={handleSubmitProjectDeadlineExtension}
        isSubmitting={isSubmitLoading}
        isRevise
      />
    </>
  );
}
